import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  ActionIcon,
  Menu,
  Text,
  Paper,
  Stack,
  Skeleton,
  Textarea,
  Switch,
  Pagination,
} from '@mantine/core';
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
} from '@tabler/icons-react';
import { productsService } from '../../services/api/products';
import { useCreateMutation, useUpdateMutation, useFormModal, useTableSort, showValidationError } from '../../hooks';
import { useDisclosure } from '@mantine/hooks';
import { SearchToolbar, FormModal, SortableHeader, DetailModal, DetailField } from '../../components/shared';
import type { Product, CreateProductInput, UpdateProductInput } from '../../types';
import { useAuthStore } from '../../stores/auth';
import dayjs from 'dayjs';

const PRODUCT_STATUS_COLORS: Record<string, string> = {
  active: 'green',
  deprecated: 'gray',
};

const INITIAL_FORM_DATA: Partial<CreateProductInput> = { isActive: true, isDemo: false };

export function ProductsPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [viewModalOpened, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [viewItem, setViewItem] = useState<Product | null>(null);

  const createModal = useFormModal<CreateProductInput>({ initialData: INITIAL_FORM_DATA });
  const editModal = useFormModal<UpdateProductInput & { isActive?: boolean }, Product>({ initialData: {} });

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => productsService.list(),
  });

  const createMutation = useCreateMutation(
    (data: CreateProductInput) => productsService.create(data),
    {
      queryKeys: ['products'],
      entityName: 'Product',
      onSuccessCallback: createModal.close,
    }
  );

  const updateMutation = useUpdateMutation(
    ({ id, data }: { id: string; data: UpdateProductInput }) => productsService.update(id, data),
    {
      queryKeys: ['products'],
      entityName: 'Product',
      onSuccessCallback: editModal.close,
    }
  );

  const handleCreateSubmit = () => {
    if (!createModal.formData.name || !createModal.formData.sku) {
      showValidationError('Name and SKU are required');
      return;
    }
    createMutation.mutate(createModal.formData as CreateProductInput);
  };

  const handleEditSubmit = () => {
    if (!editModal.selectedItem) return;
    const { isActive, sku, ...rest } = editModal.formData;
    const data: UpdateProductInput = {
      ...rest,
      status: isActive ? 'active' : 'deprecated',
    };
    if (isAdmin && sku && sku !== editModal.selectedItem.sku) {
      data.sku = sku;
    }
    updateMutation.mutate({
      id: editModal.selectedItem.id,
      data,
    });
  };

  const openEdit = (product: Product) => {
    editModal.openForEdit(product, {
      name: product.name,
      sku: product.sku,
      description: product.description || '',
      isActive: product.status === 'active',
    });
  };

  const resetPage = useCallback(() => setPage(1), []);
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort<Product>(resetPage);

  const products = productsData?.products || [];
  const filteredProducts = search
    ? products.filter(
        p =>
          (p.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
          (p.sku ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : products;

  const sortAccessors: Record<string, (item: Product) => unknown> = useMemo(() => ({
    name: (p) => p.name,
    sku: (p) => p.sku,
    status: (p) => p.status,
    description: (p) => p.description,
    created: (p) => p.createdAt,
  }), []);

  const sortedProducts = sortData(filteredProducts, sortAccessors);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(sortedProducts.length / PAGE_SIZE);
  const paginatedProducts = sortedProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Products</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={createModal.open}>
          Create Product
        </Button>
      </Group>

      <SearchToolbar
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        placeholder="Search products..."
      />

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height={50} />
            ))}
          </Stack>
        ) : filteredProducts.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No products found
          </Text>
        ) : (
          <>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableHeader column="name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Name</SortableHeader>
                <SortableHeader column="sku" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>SKU</SortableHeader>
                <SortableHeader column="status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Status</SortableHeader>
                <SortableHeader column="description" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Description</SortableHeader>

                <SortableHeader column="created" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Created</SortableHeader>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedProducts.map(product => (
                <Table.Tr key={product.id} onClick={() => { setViewItem(product); openViewModal(); }} style={{ cursor: 'pointer' }}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm" fw={500}>
                        {product.name}
                      </Text>
                      {product.isDemo && (
                        <Badge color="violet" variant="light" size="xs">
                          Demo
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="gray">
                      {product.sku}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={PRODUCT_STATUS_COLORS[product.status] || 'gray'} variant="light">
                      {product.status === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {product.description || '—'}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {dayjs(product.createdAt).format('MMM D, YYYY')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withArrow>
                      <Menu.Target>
                        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => openEdit(product)}
                        >
                          Edit
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" c="dimmed">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedProducts.length)}–{Math.min(page * PAGE_SIZE, sortedProducts.length)} of {sortedProducts.length}
            </Text>
            {totalPages > 1 && (
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            )}
          </Group>
          </>
        )}
      </Paper>

      {/* View Product Modal */}
      {viewItem && (
        <DetailModal
          opened={viewModalOpened}
          onClose={closeViewModal}
          title="Product Details"
          recordId={viewItem.id}
          actions={[
            {
              label: 'Edit',
              icon: <IconEdit size={16} />,
              onClick: () => { closeViewModal(); openEdit(viewItem); },
            },
          ]}
        >
          <DetailField label="Name" value={viewItem.name} />
          <DetailField label="SKU" value={
            <Badge variant="light" color="gray">{viewItem.sku}</Badge>
          } />
          <DetailField label="Status" value={
            <Badge color={PRODUCT_STATUS_COLORS[viewItem.status] || 'gray'} variant="light">{viewItem.status === 'active' ? 'Active' : 'Inactive'}</Badge>
          } />
          <DetailField label="Demo" value={
            <Badge color={viewItem.isDemo ? 'violet' : 'gray'} variant="light">{viewItem.isDemo ? 'Yes' : 'No'}</Badge>
          } />
          <DetailField label="Description" value={viewItem.description} multiline />
          <DetailField label="Offline Grace" value={viewItem.offlineGracePeriodHours ? `${viewItem.offlineGracePeriodHours} hours` : undefined} />
          <DetailField label="Created" value={dayjs(viewItem.createdAt).format('MMM D, YYYY h:mm A')} />
          <DetailField label="Updated" value={dayjs(viewItem.updatedAt).format('MMM D, YYYY h:mm A')} />
        </DetailModal>
      )}

      {/* Create Product Modal */}
      <FormModal
        opened={createModal.isOpen}
        onClose={createModal.close}
        title="Create Product"
        isLoading={createMutation.isPending}
        onSubmit={handleCreateSubmit}
        submitLabel="Create"
      >
        <TextInput
          label="Name"
          placeholder="Product name"
          required
          value={createModal.formData.name || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, name: e.target.value })}
        />
        <TextInput
          label="SKU"
          placeholder="PROD-001"
          required
          value={createModal.formData.sku || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, sku: e.target.value })}
        />
        <Textarea
          label="Description"
          placeholder="Product description"
          value={createModal.formData.description || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, description: e.target.value })}
          minRows={3}
        />
        <Switch
          label="Active"
          description="Inactive products cannot have new licenses created"
          checked={createModal.formData.isActive ?? true}
          onChange={e => createModal.setFormData({ ...createModal.formData, isActive: e.currentTarget.checked })}
        />
        <Switch
          label="Demo product"
          description="Licenses for this product unlock the SafeLoad demo simulator. Cannot be changed after creation."
          checked={createModal.formData.isDemo ?? false}
          onChange={e => createModal.setFormData({ ...createModal.formData, isDemo: e.currentTarget.checked })}
        />
      </FormModal>

      {/* Edit Product Modal */}
      <FormModal
        opened={editModal.isOpen}
        onClose={editModal.close}
        title="Edit Product"
        isLoading={updateMutation.isPending}
        onSubmit={handleEditSubmit}
        submitLabel="Save Changes"
      >
        <TextInput
          label="Name"
          placeholder="Product name"
          required
          value={editModal.formData.name || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, name: e.target.value })}
        />
        <TextInput
          label="SKU"
          disabled={!isAdmin}
          description={isAdmin ? undefined : 'Admin access required to change after creation'}
          value={editModal.formData.sku || editModal.selectedItem?.sku || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, sku: e.target.value })}
        />
        <Textarea
          label="Description"
          placeholder="Product description"
          value={editModal.formData.description || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, description: e.target.value })}
          minRows={3}
        />
        <Switch
          label="Active"
          description="Inactive products cannot have new licenses created"
          checked={editModal.formData.isActive ?? true}
          onChange={e => editModal.setFormData({ ...editModal.formData, isActive: e.currentTarget.checked })}
        />
        <Switch
          label="Demo product"
          checked={editModal.selectedItem?.isDemo ?? false}
          disabled
          description="Cannot change after creation"
        />
      </FormModal>
    </Container>
  );
}
